import { io, Socket } from 'socket.io-client';

// Determine the WebSocket URL based on the current hostname
const getWsUrl = () => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_WS_URL || 'http://localhost:3001';
  } else if (hostname === 'app.audiomap.io') {
    return 'https://app.audiomap.io';
  } else {
    // Fallback for other environments
    return process.env.REACT_APP_WS_URL || `https://${hostname}`;
  }
};

const WS_URL = getWsUrl();

class WebSocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<Function>> = new Map();

  connect() {
    if (this.socket?.connected) return;

    const token = localStorage.getItem('auth_token');

    this.socket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      auth: {
        token: token
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
    });

    this.socket.on('connect', () => {
      console.log('[WebSocket] Connected to', WS_URL);
      this.emit('connected', true);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[WebSocket] Disconnected:', reason);
      this.emit('connected', false);
    });

    this.socket.on('connect_error', (error) => {
      console.error('[WebSocket] Connection error:', error.message);
      this.emit('error', error);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`[WebSocket] Reconnected after ${attemptNumber} attempts`);
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`[WebSocket] Reconnection attempt #${attemptNumber}`);
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('[WebSocket] Reconnection error:', error.message);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('[WebSocket] Reconnection failed after max attempts');
    });

    // Handle real-time measurements
    this.socket.on('measurements', (data) => {
      this.emit('measurements', data);
    });

    // Handle violation alerts
    this.socket.on('violation', (data) => {
      this.emit('violation', data);
    });

    // Handle device status updates
    this.socket.on('deviceStatus', (data) => {
      this.emit('deviceStatus', data);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  subscribeToVenue(venueId: string) {
    if (this.socket?.connected) {
      this.socket.emit('subscribe', { venueId });
    }
  }

  subscribeToDevice(deviceId: string) {
    if (this.socket?.connected) {
      this.socket.emit('subscribe', { deviceId });
    }
  }

  on(event: string, callback: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback: Function) {
    this.listeners.get(event)?.delete(callback);
  }

  private emit(event: string, data: any) {
    this.listeners.get(event)?.forEach(callback => callback(data));
  }
}

export default new WebSocketService();